import React, { useState, useEffect, useCallback } from "react";
import * as Mui from "@mui/material";
import { CheckCircle, Visibility } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { themes } from "services/constants";
import { showToast } from "services/utils/Status";
import { setPayslipTemplateId } from "services/Redux/userManagament";
import { setCompanyDetails } from "services/Redux/userToken";
import { customGetAPI, customAPI } from "services/utils/CustomAPI";
import { isSpecificCompany } from "services/utils/payslipTemplate";
import { getTemplateById } from "../../../../PayslipTemplates/templates";
import { Loader } from "components/Loader";
import { TemplatePreviewDialog } from "./TemplatePreviewDialog";

export const PaySlip = () => {
  const dispatch = useDispatch();
  const { domain, topbarCompanyId, topbarCompanyName, companyDetails } = useSelector(
    (state) => state.tokenReducer,
  );

  const [templates, setTemplates] = useState([]);
  const [activeCode, setActiveCode] = useState(null);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [applyingId, setApplyingId] = useState(null);
  const [companyAddress, setCompanyAddress] = useState("");

  const companySelected = isSpecificCompany(topbarCompanyId);

  // Merge backend template metadata (id, code, name, description) with the
  // locally-defined PDF renderers, matched by `code`.
  const withRenderers = useCallback((rows) => {
    return (Array.isArray(rows) ? rows : []).map((row) => {
      const local = getTemplateById(row.code);
      return {
        ...row,
        SingleDocument: local.SingleDocument,
        BulkDocument: local.BulkDocument,
      };
    });
  }, []);

  const fetchData = useCallback(async () => {
    if (!companySelected) {
      setTemplates([]);
      setActiveCode(null);
      return;
    }
    try {
      setIsLoading(true);
      const [templatesRes, companyRes] = await Promise.all([
        customGetAPI(`${domain}payroll/setup/payslip-template/`),
        customGetAPI(`${domain}company/${topbarCompanyId}/`),
      ]);
      if (templatesRes?.ok) {
        setTemplates(withRenderers(templatesRes.responseDetails));
      }
      if (companyRes?.ok) {
        const code = companyRes.responseDetails?.payslip_template_code || null;
        setActiveCode(code);
        // Keep the rest of the app (payslip pages) in sync with this company.
        if (code) dispatch(setPayslipTemplateId(code));
        setCompanyAddress(
          companyRes.responseDetails?.address ||
            companyRes.responseDetails?.company_address ||
            "",
        );
      }
    } catch (error) {
      console.error("[PaySlip] failed to load payslip templates:", error);
      showToast("error", "Couldn't load payslip templates. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [companySelected, domain, topbarCompanyId, dispatch, withRenderers]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleApply = async (template) => {
    if (!template || !companySelected) return;
    try {
      setApplyingId(template.id);
      const response = await customAPI(
        `${domain}company/${topbarCompanyId}/`,
        "PATCH",
        { payslip_template: template.id },
      );
      if (response?.ok) {
        setActiveCode(template.code);
        dispatch(setPayslipTemplateId(template.code));
        // My Workspace resolves the template from the company list's
        // payslip_template_code, so keep that cached list in step too.
        const patchedCompanyDetails = (companyDetails ?? []).map((company) =>
          String(company?.id) === String(topbarCompanyId)
            ? {
                ...company,
                payslip_template: template.id,
                payslip_template_code: template.code,
                payslip_template_name: template.name ?? company?.payslip_template_name,
              }
            : company,
        );
        dispatch(setCompanyDetails(patchedCompanyDetails));
        localStorage.setItem(
          "companyDetails",
          JSON.stringify(patchedCompanyDetails),
        );
        showToast("success", "Payslip template applied");
        setPreviewTemplate(null);
      }
    } catch (error) {
      console.error("[PaySlip] failed to apply payslip template:", error);
      showToast(
        "error",
        "Couldn't apply the payslip template. Please try again.",
      );
    } finally {
      setApplyingId(null);
    }
  };

  return (
    <React.Fragment>
      <Mui.Box sx={{ mb: 2 }}>
        <Mui.Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
          Pay Slip
        </Mui.Typography>
        <Mui.Typography variant="body2" color="text.secondary">
          Choose the payslip template used whenever a payslip is viewed or
          downloaded
          {companySelected ? ` for ${topbarCompanyName}` : ""}.
        </Mui.Typography>
      </Mui.Box>

      {!companySelected ? (
        <Mui.Alert severity="info" variant="outlined">
          Select a specific company from the top bar to configure its payslip
          template.
        </Mui.Alert>
      ) : (
        <Mui.Paper sx={{ width: "100%" }}>
          <Mui.Box sx={{ p: 2 }}>
            <Mui.Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {templates.length} Payslip Template Records
            </Mui.Typography>
          </Mui.Box>
          {isLoading ? (
            <Mui.Box sx={{ p: 4 }}>
              <Loader info="Loading payslip templates..." />
            </Mui.Box>
          ) : (
            <Mui.Table className="border">
              <Mui.TableHead>
                <Mui.TableRow>
                  <Mui.TableCell
                    sx={{ color: themes.headLine, fontWeight: "bold" }}
                  >
                    Template Name
                  </Mui.TableCell>
                  <Mui.TableCell
                    sx={{ color: themes.headLine, fontWeight: "bold" }}
                  >
                    Description
                  </Mui.TableCell>
                  <Mui.TableCell
                    sx={{ color: themes.headLine, fontWeight: "bold" }}
                  >
                    Status
                  </Mui.TableCell>
                  <Mui.TableCell
                    align="right"
                    sx={{ color: themes.headLine, fontWeight: "bold" }}
                  >
                    Action
                  </Mui.TableCell>
                </Mui.TableRow>
              </Mui.TableHead>
              <Mui.TableBody>
                {templates.map((template) => {
                  const isActive = template.code === activeCode;
                  return (
                    <Mui.TableRow
                      key={template.id}
                      hover
                      sx={{ cursor: "pointer" }}
                      onClick={() => setPreviewTemplate(template)}
                    >
                      <Mui.TableCell sx={{ fontWeight: 600 }}>
                        {template.name}
                      </Mui.TableCell>
                      <Mui.TableCell>{template.description}</Mui.TableCell>
                      <Mui.TableCell>
                        {isActive ? (
                          <Mui.Chip
                            icon={<CheckCircle />}
                            label="Active"
                            color="success"
                            size="small"
                            variant="outlined"
                          />
                        ) : (
                          <Mui.Chip
                            label="Inactive"
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </Mui.TableCell>
                      <Mui.TableCell align="right">
                        <Mui.Button
                          size="small"
                          startIcon={<Visibility />}
                          onClick={(event) => {
                            event.stopPropagation();
                            setPreviewTemplate(template);
                          }}
                          sx={{ textTransform: "capitalize" }}
                        >
                          View
                        </Mui.Button>
                      </Mui.TableCell>
                    </Mui.TableRow>
                  );
                })}
              </Mui.TableBody>
            </Mui.Table>
          )}
        </Mui.Paper>
      )}

      <TemplatePreviewDialog
        open={!!previewTemplate}
        template={previewTemplate}
        isActive={previewTemplate?.code === activeCode}
        isApplying={applyingId === previewTemplate?.id}
        onApply={() => handleApply(previewTemplate)}
        onClose={() => setPreviewTemplate(null)}
        companyName={topbarCompanyName}
        companyAddress={companyAddress}
      />
    </React.Fragment>
  );
};
